import { useCallback, useState } from 'react';
import moment from 'moment-timezone';
import {
  useQueryParams,
  StringParam,
  ArrayParam,
  withDefault,
  BooleanParam,
} from 'use-query-params';

import { TaskType } from '@scale/shared-types';
import { fromUnix, toUnix } from 'dashboard/lib/time';
import { Tags } from '../components/Filters/TagFilter';

type IBatches = string[];

type IType = TaskType[];

type IStatus = $FIXME[];

export type IDateFilter = $FIXME;

type IAuditStatus = $FIXME[];

type ITags = Tags[];

type IProject = string | undefined;

export type IDate = moment.Moment | undefined;

type IMarkedForHumanReview = boolean | undefined;

const DateParam = { encode: toUnix, decode: fromUnix };

const DefaultArrayParam = withDefault<any, any>(ArrayParam, []);

const DateFilterParam = withDefault(StringParam, 'created');

export interface IFilters {
  project: string | undefined;
  batches: IBatches;
  status: IStatus;
  auditStatus: IAuditStatus;
  tags: ITags;
  type: IType;
  dateFilter: IDateFilter;
  dateFrom: IDate;
  dateTo: IDate;
  markedForHumanReview: IMarkedForHumanReview;
  viewingScenes?: boolean;
  showUniqueId?: boolean;
  skipped?: boolean;
  version?: string;
  pinned?: boolean;
  dataQuery?: string;
  auditorId?: string;
  chatCompetency?: string;
}

const useFilters = () => {
  const [value, set] = useQueryParams({
    type: DefaultArrayParam,
    batches: DefaultArrayParam,
    tags: DefaultArrayParam,
    status: DefaultArrayParam,
    dateFilter: DateFilterParam,
    auditStatus: DefaultArrayParam,
    dateTo: withDefault(DateParam, undefined),
    dateFrom: withDefault(DateParam, undefined),
    project: withDefault(StringParam, undefined),
    markedForHumanReview: withDefault(BooleanParam, undefined),
    viewingScenes: withDefault(BooleanParam, undefined),
    showUniqueId: withDefault(BooleanParam, false),
    skipped: withDefault(BooleanParam, undefined),
    pinned: withDefault(BooleanParam, undefined),
    dataQuery: withDefault(StringParam, undefined),
    // not sure if this collides with 'search' query param in other places?
    search: withDefault(StringParam, undefined),
    version: withDefault(StringParam, undefined),
    auditorId: withDefault(StringParam, undefined),
    chatCompetency: withDefault(StringParam, undefined),
  });

  const clear = useCallback(() => {
    set({
      type: [],
      status: [],
      batches: [],
      tags: [],
      auditStatus: [],
      dateTo: undefined,
      project: undefined,
      dateFrom: undefined,
      dateFilter: undefined,
      markedForHumanReview: undefined,
      viewingScenes: undefined,
      showUniqueId: undefined,
      skipped: undefined,
      version: undefined,
      pinned: undefined,
      dataQuery: undefined,
      auditorId: undefined,
    });
  }, [set]);

  const setDateFilter = useCallback((value: IDateFilter) => set({ dateFilter: value }), [set]);

  const setDateFrom = useCallback((value: IDate) => set({ dateFrom: value }), [set]);

  const setProject = useCallback((value: IProject) => set({ project: value }), [set]);

  const setDateTo = useCallback((value: IDate) => set({ dateTo: value }), [set]);

  const setTaskTypes = useCallback((value: IType) => set({ type: value }), [set]);

  const setStatus = useCallback((value: IStatus) => set({ status: value }), [set]);

  const setAuditStatus = useCallback((value: IAuditStatus) => set({ auditStatus: value }), [set]);

  const setBatches = useCallback((value: IBatches) => set({ batches: value }), [set]);

  const setTags = useCallback((value: ITags) => set({ tags: value }), [set]);

  const setShowUniqueId = useCallback(
    (value: IMarkedForHumanReview) => set({ showUniqueId: value }),
    [set],
  );

  const setMarkedForHumanReview = useCallback(
    (value: IMarkedForHumanReview) => set({ markedForHumanReview: value }),
    [set],
  );

  const setViewingScenes = useCallback((value?: boolean) => set({ viewingScenes: value }), [set]);

  const [showSkipped, setShowSkipped] = useState(false);

  const setSkipped = useCallback((value: boolean | undefined) => set({ skipped: value }), [set]);

  const setSearch = useCallback((value: string | undefined) => set({ search: value }), [set]);

  const setVersion = useCallback((value: string | undefined) => set({ version: value }), [set]);

  const setPinned = useCallback((value: boolean | undefined) => set({ pinned: value }), [set]);

  const setDataQuery = useCallback((value: string | undefined) => set({ dataQuery: value }), [set]);

  const setAuditorId = useCallback((value: string | undefined) => set({ auditorId: value }), [set]);

  const setChatCompetency = useCallback(
    (value: string | undefined) => set({ chatCompetency: value }),
    [set],
  );

  return {
    value: value as IFilters,
    clear,
    setProject,
    setDateFilter,
    setDateFrom,
    setDateTo,
    setTaskTypes,
    setStatus,
    setAuditStatus,
    setBatches,
    setTags,
    setMarkedForHumanReview,
    setShowUniqueId,
    showSkipped,
    setShowSkipped,
    setViewingScenes,
    setSkipped,
    setSearch,
    setVersion,
    setPinned,
    setDataQuery,
    setAuditorId,
    setChatCompetency,
  };
};

export default useFilters;
